import type { MedicationOption } from '@/types/interfaces/productDetailPage'

/** Locked Quantity pill copy, or null when the option has nothing to show. */
export function buildQuantityLabel(option: MedicationOption): string | null {
    const supplyLabel = option.supply?.display_label?.trim()
    if (supplyLabel) return supplyLabel

    const quantityPhrase = buildQuantityPhrase(option)
    const daysSupply = formatWholeUnlessFractional(option.default_days_supply)
    if (!quantityPhrase || !daysSupply) return null

    return `${quantityPhrase} · ${daysSupply} day supply`
}

/**
 * Collapsed twister summary — dose form, strength, and supply when available
 * (e.g. "Tablet · 20mg · 30 tablets · 30 day supply").
 */
export function buildSummary(option: MedicationOption): string {
    const supplyLabel = option.supply?.display_label?.trim()
    if (supplyLabel) {
        return `${capitalize(option.dose_form)} · ${option.strength} · ${supplyLabel}`
    }

    const quantityPhrase = buildQuantityPhrase(option)
    const daysSupply = formatWholeUnlessFractional(option.default_days_supply)
    const dayUnit = Number(option.default_days_supply) === 1 ? 'day' : 'days'
    const supplyClause = quantityPhrase && daysSupply ? ` · ${quantityPhrase} (${daysSupply} ${dayUnit})` : ''
    return `${capitalize(option.dose_form)} · ${option.strength}${supplyClause}`
}

/** "30 tablets" from legacy quantity fields when supply.display_label is unavailable. */
function buildQuantityPhrase(option: MedicationOption): string | null {
    if (!option.default_quantity || !option.default_quantity_unit) return null

    const quantity = formatWholeUnlessFractional(option.default_quantity)
    return `${quantity} ${option.default_quantity_unit}`
}

/** Whole numbers without a trailing ".0"; fractional values keep their serialized form. */
function formatWholeUnlessFractional(value: string | number | null): string | null {
    if (value == null) return null

    const numeric = Number(value)
    return Number.isInteger(numeric) ? String(numeric) : String(value)
}

export function capitalize(value: string): string {
    return value.length ? value.charAt(0).toUpperCase() + value.slice(1) : value
}
